import React from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Public as RegionsIcon,
  Dashboard as DashboardIcon,
  Grain as GrapeIcon,
  Business as ProducerIcon,
  WineBar as WineNameIcon,
  Store as VenueIcon,
  LocalShipping as SupplierIcon,
  Inventory as InventoryIcon,
  Assessment as ReportsIcon,
  ShoppingCart as PurchasingIcon,
  Checklist as StockCountIcon,
  Warehouse as WarehouseIcon,
  RuleOutlined as GovernanceIcon,
  PointOfSale as PosIcon,
  LocalBar as MenuIcon,
  Receipt as SalesImportIcon,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const drawerWidth = 280;

/**
 * The console's navigation, as two roots.
 *
 * WHAT THIS REPLACES, AND WHY IT WAS REBUILT RATHER THAN ADJUSTED
 *
 * The previous version had grown seven bespoke render blocks, each with its own
 * visibility rule inlined at the point of rendering. It carried a "Users"
 * section whose item list was empty, so it rendered nothing and could not be
 * seen to be dead. It nested the venue sub-nav INSIDE the Venues list behind a
 * border hack. It put a section label and a nav item both reading "Venues" on
 * screen at once. And "Inventory Governance" held global maintenance tools and
 * venue-scoped triage in one list under one heading.
 *
 * Sections are declared as data now and rendered by one loop. A section with no
 * visible items does not render, so dead entries disappear instead of leaving
 * an empty heading — the bug that hid the Users section for however long.
 *
 * THE TWO ROOTS
 *
 *   STOCK   is where stock IS. It leads with warehouses, because venue answers
 *           WHO and warehouse answers WHERE, and every stock question is a
 *           where-question. A venue with one warehouse sees one row and never
 *           notices; a company with a shared cellar sees it beside its own
 *           stores rather than buried inside whichever venue administers it.
 *
 *   VENUES  is who sells it. A PEER of Stock, not its parent. What lives here is
 *           genuinely venue-shaped — menus, pricing, POS — plus the stock
 *           OPERATIONS a venue performs, which need a venue to attribute to.
 *
 * Warehouses and Locations are gone from the venue sub-nav. A location belongs
 * to a warehouse, and reaching it by first picking an arbitrary venue is what
 * made "General Stock" land in the wrong store.
 *
 * WINE DATABASE is SYSTEM_ADMIN only (WINE-DB-NAV.1): the wine master data is
 * shared across every client, so editing it is not a per-tenant act. NAV ONLY —
 * the API still accepts these writes from ADMIN, so this hides the door without
 * locking it. Tightening the routes is a separate, deliberate change.
 */

interface NavItem {
  text: string;
  icon: React.ReactNode;
  path: string;
}

interface NavSection {
  label: string;
  items: NavItem[];
  /** Rendered one level in, under a section that is already a place. */
  indent?: boolean;
}

interface SideNavProps {
  open: boolean;
  onClose?: () => void;
  variant: 'permanent' | 'persistent' | 'temporary';
}

const SideNav: React.FC<SideNavProps> = ({ open, onClose, variant }) => {
  const theme = useTheme();
  const location = useLocation();
  const { isSystemAdmin, isVenueUser } = useAuth();

  const venueUser = isVenueUser();
  const wineDbVisible = isSystemAdmin();

  const isTemporary = variant === 'temporary';
  const handleNavClick = () => { if (isTemporary && onClose) onClose(); };
  const isActive = (path: string) => location.pathname.startsWith(path);

  // Venue context: /admin/venues/:id/... where :id is a real venue.
  const venueMatch = location.pathname.match(/^\/admin\/venues\/([^/]+)/);
  const venueId = venueMatch?.[1];
  const inVenue =
    !!venueId && venueId !== 'create'
    && !location.pathname.match(/^\/admin\/venues\/[^/]+\/edit\/?$/);

  const v = (suffix: string) => `/admin/venues/${venueId}${suffix}`;

  // ── The sections, as data ─────────────────────────────────────────────────
  const sections: NavSection[] = [
    {
      label: 'Stock',
      items: venueUser ? [] : [
        { text: 'Warehouses', icon: <WarehouseIcon />, path: '/admin/stock' },
        // The GLOBAL supplier catalogue is shared across every client, so it
        // follows the wine master data. The venue-scoped Suppliers page below is
        // untouched: that is where an admin grants a venue access to a supplier,
        // and it is per-tenant.
        ...(wineDbVisible
          ? [{ text: 'Suppliers', icon: <SupplierIcon />, path: '/admin/suppliers' }]
          : []),
      ],
    },
    {
      label: 'Venues',
      items: venueUser ? [] : [
        // "All venues", not "Venues": a section label and an item reading the
        // same word put the same string on screen twice and made it ambiguous
        // which one a reader — or a test — meant.
        { text: 'All venues', icon: <VenueIcon />, path: '/admin/venues' },
      ],
    },
    {
      label: venueUser ? 'Venue workspace' : 'This venue',
      indent: !venueUser,
      items: inVenue ? [
        { text: 'Overview',      icon: <DashboardIcon />,    path: v('') },
        { text: 'Inventory',     icon: <InventoryIcon />,    path: v('/inventory') },
        { text: 'Purchasing',    icon: <PurchasingIcon />,   path: v('/purchasing') },
        { text: 'Stock Counts',  icon: <StockCountIcon />,   path: v('/stock-counts') },
        { text: 'Sales Imports', icon: <SalesImportIcon />,  path: v('/sales-imports') },
        { text: 'Reports',       icon: <ReportsIcon />,      path: v('/reports') },
        { text: 'Menus',         icon: <MenuIcon />,         path: v('/menus') },
        { text: 'Suppliers',     icon: <SupplierIcon />,     path: v('/suppliers') },
      ] : [],
    },
    {
      label: 'Wine Database',
      items: wineDbVisible ? [
        { text: 'Dashboard',       icon: <DashboardIcon />, path: '/admin/dashboard' },
        { text: 'Wines',           icon: <WineNameIcon />,  path: '/admin/wines' },
        { text: 'Producers',       icon: <ProducerIcon />,  path: '/admin/producers' },
        { text: 'Wine Regions',    icon: <RegionsIcon />,   path: '/admin/regions' },
        { text: 'Grape Varieties', icon: <GrapeIcon />,     path: '/admin/grapes' },
        { text: 'Wine Names',      icon: <WineNameIcon />,  path: '/admin/wine-names' },
      ] : [],
    },
    {
      label: 'Data Governance',
      indent: true,
      // Label Scans and Grape Rules are deliberately absent on this release
      // branch: their screens arrive with GK-1, which is not on prod. A nav
      // entry pointing at a route that does not exist is a worse bug than a
      // missing one — it looks like the app is broken rather than incomplete.
      // Restore both when GK-1 ships.
      items: wineDbVisible ? [
        { text: 'Review Queue',    icon: <GovernanceIcon />, path: '/admin/governance/review-queue' },
        { text: 'Duplicate Wines', icon: <GovernanceIcon />, path: '/admin/governance/duplicate-wines' },
      ] : [],
    },
    {
      // Was "Inventory Governance", which held global maintenance tools and
      // venue-scoped ledger triage under one heading. They are different jobs
      // with different audiences; POS triage is admin-only on the backend, so a
      // VENUE_USER clicking it would 403.
      label: 'Maintenance',
      items: [
        ...(venueUser ? [] : [
          { text: 'Repair',      icon: <GovernanceIcon />, path: '/admin/governance/repair' },
          { text: 'Data Health', icon: <GovernanceIcon />, path: '/admin/governance/health' },
        ]),
        ...(inVenue && !venueUser ? [
          { text: 'POS Governance', icon: <PosIcon />,        path: v('/pos-inventory/governance') },
          { text: 'Balance Drift',  icon: <GovernanceIcon />, path: v('/inventory/drift') },
        ] : []),
      ],
    },
  ];

  // A VENUE_USER who is not inside a venue has somewhere to go, and it is the
  // only thing they can do — so it replaces the sections rather than joining
  // them.
  const strandedVenueUser = venueUser && !inVenue;

  const renderItem = (item: NavItem, indent: boolean) => {
    const active = isActive(item.path);
    return (
      <ListItemButton
        component={Link as React.ElementType}
        to={item.path}
        key={item.path}
        selected={active}
        onClick={handleNavClick}
        sx={{
          mb: 0.5,
          borderRadius: 2,
          py: 1.125,
          px: 2,
          pl: indent ? 3 : 2,
          minHeight: 44,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            transform: 'translateX(4px)',
          },
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            color: theme.palette.primary.main,
            fontWeight: 600,
            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.16) },
            '& .MuiListItemIcon-root': { color: theme.palette.primary.main },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 36,
            color: active ? theme.palette.primary.main : theme.palette.text.secondary,
            transition: 'color 0.2s ease-in-out',
            '& .MuiSvgIcon-root': { fontSize: indent ? '1.1rem' : '1.25rem' },
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.text}
          slotProps={{ primary: { sx: { fontSize: '0.875rem', fontWeight: active ? 600 : 500 } } }}
        />
      </ListItemButton>
    );
  };

  const SectionLabel: React.FC<{ label: string; indent?: boolean }> = ({ label, indent }) => (
    <Typography
      variant="caption"
      sx={{
        px: 2,
        pt: indent ? 1.5 : 2,
        pb: 0.5,
        ml: indent ? 1 : 0,
        display: 'block',
        fontWeight: 700,
        fontSize: indent ? '0.65rem' : '0.7rem',
        letterSpacing: indent ? '0.07em' : '0.08em',
        textTransform: 'uppercase',
        color: alpha(theme.palette.text.secondary, indent ? 0.5 : 0.7),
      }}
    >
      {label}
    </Typography>
  );

  // Only sections that have something in them. An empty section used to render
  // its heading anyway, which is how a dead "Users" list stayed on screen.
  const visible = sections.filter(s => s.items.length > 0);

  // Wine-identity areas keep the Nota Wine logo; everywhere else is Nota Stock.
  const onWineIdentity = [
    '/admin/regions', '/admin/grapes', '/admin/producers',
    '/admin/wine-names', '/admin/wines', '/admin/governance', '/admin/dashboard',
  ].some(p => location.pathname.startsWith(p));

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          ...(isTemporary
            ? { width: drawerWidth, boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)' }
            : {
                width: drawerWidth - 32,
                position: 'static',
                height: 'calc(100vh - 32px)',
                borderRadius: 2,
                m: 2,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                backdropFilter: 'blur(10px)',
              }),
          boxSizing: 'border-box',
          border: 'none',
          backgroundImage: 'none',
          backgroundColor: theme.palette.background.paper,
          overflowY: 'auto',
        },
      }}
    >
      <Box
        sx={{
          p: 3,
          pt: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          minHeight: 80,
        }}
      >
        <img
          src={onWineIdentity ? '/assets/logo-nota-wine.svg' : '/assets/logo-nota-stock.svg'}
          alt={onWineIdentity ? 'Nota Wine' : 'Nota Stock'}
          style={{ height: '48px', width: 'auto', maxWidth: '100%' }}
        />
      </Box>

      <Box sx={{ px: 1, pt: 1, pb: 2 }}>
        {strandedVenueUser ? (
          <List disablePadding sx={{ px: 1 }}>
            {renderItem(
              { text: 'Select a venue', icon: <VenueIcon />, path: '/admin/my-venues' },
              false,
            )}
          </List>
        ) : (
          visible.map((section, i) => (
            <React.Fragment key={section.label}>
              <SectionLabel label={section.label} indent={section.indent} />
              <List disablePadding sx={{ px: 1, pb: 1 }}>
                {section.items.map(item => renderItem(item, Boolean(section.indent)))}
              </List>
              {/* A rule between roots, not between a section and its own
                  sub-section — the indent already says they belong together. */}
              {i < visible.length - 1 && !visible[i + 1].indent && (
                <Divider sx={{ mx: 1, opacity: 0.5 }} />
              )}
            </React.Fragment>
          ))
        )}
      </Box>
    </Drawer>
  );
};

export default SideNav;
